import { FormEvent, useEffect, useState } from 'react';
import { api, Company, Contact, Deal, Pipeline } from '../api';
import { ErrorBox, Field, SourceBadge } from '../components/ui';
import { useMembers } from '../components/TaskList';

export interface DealInput {
  title: string; company_id: string | null; value: number | null; currency: 'HUF' | 'EUR'; probability: number | null;
  expected_close: string | null; owner_user_id?: string | null; next_step: string | null; tags: string[]; contact_ids: string[];
  pipeline_id?: string; stage_id?: string;
}

/** Deal űrlap. Új dealnél pipeline és kezdő lépés választható; a cég kereshető, a kontaktok a cégéi közül. */
export function DealForm({ initial, pipelines, fixedCompany, onSubmit, onCancel, error, busy }: {
  initial?: Deal; pipelines?: Pipeline[]; fixedCompany?: { id: string; name: string }; onSubmit: (v: DealInput) => void; onCancel?: () => void; error?: unknown; busy?: boolean;
}) {
  const members = useMembers();
  const firstPipe = pipelines?.find((p) => p.is_default) ?? pipelines?.[0];
  const [v, setV] = useState({
    title: initial?.title ?? '', value: initial?.value != null ? String(initial.value) : '', currency: initial?.currency ?? 'HUF',
    probability: initial?.probability != null ? String(initial.probability) : '', expected_close: initial?.expected_close ?? '',
    owner_user_id: initial?.owner_user_id ?? '', next_step: initial?.next_step ?? '', tags: (initial?.tags ?? []).join(', '),
    pipeline_id: firstPipe?.id ?? '', stage_id: '',
  });
  const [company, setCompany] = useState<{ id: string; name: string } | null>(
    fixedCompany ?? (initial?.company_id ? { id: initial.company_id, name: initial.company_name ?? '' } : null));
  const [cq, setCq] = useState('');
  const [hits, setHits] = useState<Company[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [picked, setPicked] = useState<string[]>((initial?.contacts ?? []).map((k) => k.id));
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });
  const fs = initial?.field_sources ?? {};

  useEffect(() => {
    if (!cq.trim() || company) { setHits([]); return; }
    const t = setTimeout(() => api<{ items: Company[] }>(`/companies?query=${encodeURIComponent(cq)}&limit=8`).then((r) => setHits(r.items)).catch(() => setHits([])), 200);
    return () => clearTimeout(t);
  }, [cq, company]);
  useEffect(() => {
    if (!company) { setContacts([]); return; }
    api<{ items: Contact[] }>(`/contacts?company_id=${company.id}&limit=100`).then((r) => setContacts(r.items)).catch(() => setContacts([]));
  }, [company?.id]);

  const pipe = pipelines?.find((p) => p.id === v.pipeline_id);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const num = (x: string) => (x.trim() === '' ? null : Number(x.replace(/\s/g, '').replace(',', '.')));
    onSubmit({
      title: v.title.trim(), company_id: company?.id ?? null, value: num(v.value), currency: v.currency as 'HUF' | 'EUR',
      probability: num(v.probability), expected_close: v.expected_close || null, owner_user_id: v.owner_user_id || (initial ? null : undefined),
      next_step: v.next_step.trim() || null, tags: v.tags.split(',').map((t) => t.trim()).filter(Boolean),
      contact_ids: company ? picked : [],
      ...(!initial && v.pipeline_id ? { pipeline_id: v.pipeline_id, ...(v.stage_id ? { stage_id: v.stage_id } : {}) } : {}),
    });
  };
  return (
    <form className="form" onSubmit={submit}>
      <div className="grid2">
        <Field label="Megnevezés *"><input value={v.title} onChange={set('title')} required maxLength={300} placeholder="pl. Raktárszoftver bevezetés" /><SourceBadge src={fs.title} /></Field>
        <Field label="Cég">
          {company ? (
            <div className="chip">{company.name}{!fixedCompany && <button type="button" className="link" onClick={() => { setCompany(null); setPicked([]); }} aria-label="Cég törlése">×</button>}</div>
          ) : (
            <div className="combo">
              <input value={cq} onChange={(e) => setCq(e.target.value)} placeholder="Keresés cégnév szerint…" />
              {hits.length > 0 && <ul className="combo-list">{hits.map((h) => <li key={h.id}><button type="button" className="link" onClick={() => { setCompany({ id: h.id, name: h.name }); setCq(''); }}>{h.name}</button></li>)}</ul>}
            </div>
          )}
        </Field>
        <Field label="Érték"><div className="row"><input inputMode="decimal" value={v.value} onChange={set('value')} placeholder="0" />
          <select value={v.currency} onChange={set('currency')} aria-label="Pénznem" style={{ width: 90 }}><option>HUF</option><option>EUR</option></select></div><SourceBadge src={fs.value} /></Field>
        <Field label="Várható zárás"><input type="date" value={v.expected_close} onChange={set('expected_close')} /><SourceBadge src={fs.expected_close} /></Field>
        <Field label="Valószínűség (%)" hint="üresen a lépés szerinti"><input inputMode="numeric" value={v.probability} onChange={set('probability')} /></Field>
        <Field label="Felelős">
          <select value={v.owner_user_id} onChange={set('owner_user_id')}>
            <option value="">{initial ? '–' : 'én'}</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </Field>
        {!initial && pipelines && pipelines.length > 0 && (
          <>
            {pipelines.length > 1 && (
              <Field label="Pipeline"><select value={v.pipeline_id} onChange={(e) => setV({ ...v, pipeline_id: e.target.value, stage_id: '' })}>
                {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
            )}
            <Field label="Kezdő lépés"><select value={v.stage_id} onChange={set('stage_id')}>
              <option value="">első lépés</option>
              {pipe?.stages.filter((s) => s.kind === 'open').map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></Field>
          </>
        )}
        <Field label="Címkék" hint="vesszővel elválasztva"><input value={v.tags} onChange={set('tags')} /></Field>
      </div>
      <Field label="Következő lépés"><input value={v.next_step} onChange={set('next_step')} maxLength={500} placeholder="pl. Demó egyeztetése a pénzügyi vezetővel" /><SourceBadge src={fs.next_step} /></Field>
      {company && contacts.length > 0 && (
        <Field label="Kontaktok a dealben">
          <div className="checks">
            {contacts.map((k) => (
              <label key={k.id} className="check"><input type="checkbox" checked={picked.includes(k.id)}
                onChange={(e) => setPicked(e.target.checked ? [...picked, k.id] : picked.filter((x) => x !== k.id))} /> {k.full_name}{k.position ? <span className="muted"> · {k.position}</span> : null}</label>
            ))}
          </div>
        </Field>
      )}
      <ErrorBox error={error} />
      <div className="actions">
        <button type="submit" disabled={busy}>Mentés</button>
        {onCancel && <button type="button" className="secondary" onClick={onCancel}>Mégse</button>}
      </div>
    </form>
  );
}
